import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Building2, Check, Search, X } from 'lucide-react';
import type { Lead } from '../../../lib/leadsApi';
import { useClients } from '../../../hooks/useClients';
import { useUpdateLeadById } from '../../../hooks/useLeads';
import { Popover } from '../../tasks/linear/Popover';

/**
 * CLE-192 — searchable client link for the lead detail sidebar. Picks an
 * existing client to associate with the lead (used later when converting to a
 * project, which inherits the lead's client). A linked client — e.g. the one
 * the WON rule created — opens from the arrow beside it.
 */
export function LeadClientEditor({ lead }: { lead: Lead }) {
  const update = useUpdateLeadById();

  function setClient(clientId: string | null) {
    update.mutate({ id: lead.id, clientId });
  }

  return (
    <span className="inline-flex max-w-full items-center gap-0.5">
      <Popover
        width={260}
        align="end"
        trigger={({ toggle, ref }) => (
          <button
            ref={ref}
            type="button"
            onClick={toggle}
            className="inline-flex max-w-full items-center gap-1.5 rounded px-1.5 py-0.5 text-[13px] hover:bg-gray-200"
            aria-label="Asociază client"
          >
            <Building2 size={13} className="shrink-0 text-gray-500" />
            <span className={`truncate ${lead.clientId ? 'text-gray-700' : 'text-gray-400'}`}>
              <ClientName lead={lead} />
            </span>
          </button>
        )}
      >
        {(close) => <ClientMenu selectedId={lead.clientId} onPick={setClient} close={close} />}
      </Popover>
      {lead.clientId && (
        <Link
          to={`/clients/${lead.clientId}`}
          aria-label="Deschide clientul"
          title="Deschide clientul"
          className="shrink-0 rounded p-0.5 text-gray-500 hover:bg-gray-200 hover:text-brand-700"
        >
          <ArrowUpRight size={14} />
        </Link>
      )}
    </span>
  );
}

function ClientName({ lead }: { lead: Lead }) {
  // Resolve the linked client's name from the list cache; fall back to a
  // neutral label while loading or when none is linked.
  const { data } = useClients({ limit: 200 });
  if (!lead.clientId) return <>Fără client</>;
  const found = data?.data.find((c) => c.id === lead.clientId);
  return <>{found?.name ?? 'Client asociat'}</>;
}

function ClientMenu({
  selectedId,
  onPick,
  close,
}: {
  selectedId: string | null;
  onPick: (id: string | null) => void;
  close: () => void;
}) {
  const [query, setQuery] = useState('');
  const { data } = useClients({ search: query || undefined, limit: 50 });

  const clients = useMemo(() => data?.data ?? [], [data]);

  function pick(id: string | null) {
    close();
    if (id === selectedId) return;
    onPick(id);
  }

  return (
    <div className="flex flex-col">
      <div className="flex items-center gap-2 border-b border-border px-3 py-2">
        <Search size={14} className="text-gray-400" />
        <input
          autoFocus
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Caută clienți…"
          className="w-full bg-transparent text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none"
        />
      </div>
      <div className="max-h-64 overflow-y-auto py-1">
        {clients.map((c) => (
          <button
            key={c.id}
            type="button"
            onClick={() => pick(c.id)}
            className="ui-menu-item justify-between"
          >
            <span className="truncate">{c.name}</span>
            {c.id === selectedId && <Check size={14} className="text-gray-400" />}
          </button>
        ))}
        {clients.length === 0 && (
          <p className="px-3 py-2 text-sm text-gray-400">Niciun client găsit.</p>
        )}
      </div>
      {selectedId && (
        <div className="border-t border-border py-1">
          <button type="button" onClick={() => pick(null)} className="ui-menu-item text-gray-500">
            <X size={14} /> Elimină clientul
          </button>
        </div>
      )}
    </div>
  );
}
